import { Box } from '@mui/material';
import { useContentElement } from '@plugins/next-cms-core';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

export default function JobPostingStructuredData(props) {
  const { data, context: { page }, appData: { pageUrls } } = props;
  const router = useRouter();
  const { elementData } = useContentElement(
    data,
    JobPostingStructuredData.dataSchema,
  );

  const {
    name,
    employmentType,
    minSalary,
    maxSalary,
    salaryUnit,
    validThrough,
    jobPostingDescription,
    branches,
  } = elementData;

  const pageId = pageUrls.find((url) => url.locale === router.locale)?.id;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: name,
    datePosted: page.publishedAt,
    description: jobPostingDescription,
    employmentType: EMPLOYMENT_TYPE[employmentType],
    hiringOrganization: {
      '@type': 'Organization',
      name: 'CSB Schimmel Automobile GmbH',
      sameAs: 'https://www.schimmel-automobile.de/',
      logo: 'https://www.schimmel-automobile.de/cdn-cgi/image/width=1080,quality=75,format=auto//images/brand/brand-logo-default.svg',
    },
    identifier: {
      '@type': 'PropertyValue',
      name: 'CSB Schimmel Automobile GmbH',
      value: `JOB-${pageId}-${page.publishedAt}`,
    },
    ...(validThrough ? { validThrough } : {}),
    ...(minSalary > 0 || maxSalary > 0
      ? {
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'EUR',
          value: {
            '@type': 'QuantitativeValue',
            ...(minSalary > 0 ? { minValue: minSalary } : {}),
            ...(maxSalary > 0 ? { maxValue: maxSalary } : {}),
            ...(salaryUnit ? { unitText: salaryUnit.toUpperCase() } : {}),
          },
        },
      }
      : {}),
    ...(branches?.data?.length
      ? {
        jobLocation: branches.data.map((branch) => ({
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: branch.attributes.addressStreet,
            postalCode: branch.attributes.addressZipcode,
            addressLocality: branch.attributes.addressCity,
            addressRegion: 'BE',
            addressCountry: COUNTRY[branch.attributes.addressCountry],
          },
        })),
      }
      : {}),
  };

  return (
    <Box>
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        type="application/ld+json"
      />
    </Box>
  );
}

JobPostingStructuredData.typeName = 'ComponentContentJobPostingStructuredData'; // Strapi element type
JobPostingStructuredData.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    employmentType: PropTypes.oneOf([
      'fullTime',
      'partTime',
      'contract',
      'temporary',
      'seasonal',
      'internship',
    ]),
    minSalary: PropTypes.number,
    maxSalary: PropTypes.number,
    salaryUnit: PropTypes.oneOf(['month', 'year']),
    description: PropTypes.string,
    branches: PropTypes.arrayOf(
      PropTypes.shape({
        addressStreet: PropTypes.string,
        addressZipcode: PropTypes.string,
        addressCity: PropTypes.string,
        addressCountry: PropTypes.string,
      }),
    ),
  }).isRequired,
};

JobPostingStructuredData.graphQlSchema = `
... on ${JobPostingStructuredData.typeName} {
  id
  name
  employmentType
  minSalary
  maxSalary
  salaryUnit
  validThrough
  jobPostingDescription: description
  branches {
    data {
      id
      attributes {
        addressStreet
        addressZipcode
        addressCity
        addressCountry
      }
    }
  }
}
`;

const EMPLOYMENT_TYPE = {
  fullTime: 'FULL_TIME',
  partTime: 'PART_TIME',
  contract: 'CONTRACTOR',
  temporary: 'TEMPORARY',
  seasonal: 'SEASONAL',
  internship: 'INTERN',
};

const COUNTRY = {
  Deutschland: 'DE',
};
